import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import {
  ChevronUp,
  ChevronDown,
  Edit,
  Trash2,
  Copy,
  MoreHorizontal,
  Package,
  Eye,
  Grid,
  List,
  ChevronLeft,
  ChevronRight,
  Star,
  Printer,
} from 'lucide-react';
import { clsx } from 'clsx';
import type { Product } from '../types';

// Hook to detect mobile screen
function useIsMobile(breakpoint = 1024) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [breakpoint]);

  return isMobile;
}

interface ProductsTableProps {
  products: Product[];
  total: number;
  isLoading: boolean;
  selectedIds: string[];
  onSelectAll: (selected: boolean) => void;
  onSelectOne: (id: string, selected: boolean) => void;
  onEdit: (product: Product) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  onView?: (product: Product) => void;
  onPrintLabel?: (product: Product) => void;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort: (field: string) => void;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  viewMode: 'table' | 'grid';
  onViewModeChange: (mode: 'table' | 'grid') => void;
}

const PAGE_SIZES = [10, 20, 50, 100];

export function ProductsTable({
  products,
  total,
  isLoading,
  selectedIds,
  onSelectAll,
  onSelectOne,
  onEdit,
  onDelete,
  onDuplicate,
  onView,
  onPrintLabel,
  sortBy,
  sortOrder,
  onSort,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  viewMode,
  onViewModeChange,
}: ProductsTableProps) {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const menuButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  
  // Auto-switch to grid on mobile
  const isMobile = useIsMobile();
  const effectiveViewMode = isMobile ? 'grid' : viewMode;

  // Update menu position when openMenuId changes
  useEffect(() => {
    if (openMenuId) {
      const button = menuButtonRefs.current.get(openMenuId);
      if (button) {
        const rect = button.getBoundingClientRect();
        setMenuPosition({
          top: rect.bottom + 4,
          left: rect.right - 140, // 140px is min-width of menu
        });
      }
    } else {
      setMenuPosition(null);
    }
  }, [openMenuId]);

  const handleMenuButtonClick = (productId: string, buttonEl: HTMLButtonElement) => {
    menuButtonRefs.current.set(productId, buttonEl);
    setOpenMenuId(openMenuId === productId ? null : productId);
  };

  const allSelected = products.length > 0 && selectedIds.length === products.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < products.length;
  const totalPages = Math.ceil(total / pageSize);

  const SortIcon = ({ field }: { field: string }) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? (
      <ChevronUp size={16} className="text-orange-400" />
    ) : (
      <ChevronDown size={16} className="text-orange-400" />
    );
  };

  const SortableHeader = ({
    field,
    children,
    className,
  }: {
    field: string;
    children: React.ReactNode;
    className?: string;
  }) => (
    <th
      className={clsx(
        'px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider cursor-pointer hover:text-slate-900 transition-colors',
        className
      )}
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <SortIcon field={field} />
      </div>
    </th>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="text-center py-12 text-slate-600">
        <Package size={48} className="mx-auto mb-4 opacity-50" />
        <p>No products found</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* View Mode Toggle & Pagination Info */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">
            Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of {total}
          </span>
        </div>
        {/* Hide view toggle on mobile - always grid there */}
        <div className="hidden lg:flex items-center gap-2">
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => onViewModeChange('table')}
              className={clsx(
                'p-1.5 rounded transition-colors',
                viewMode === 'table' ? 'bg-gray-300 text-slate-900' : 'text-slate-600 hover:text-slate-900'
              )}
            >
              <List size={16} />
            </button>
            <button
              onClick={() => onViewModeChange('grid')}
              className={clsx(
                'p-1.5 rounded transition-colors',
                viewMode === 'grid' ? 'bg-gray-300 text-slate-900' : 'text-slate-600 hover:text-slate-900'
              )}
            >
              <Grid size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Table View (desktop) / Grid View (mobile + when selected) */}
      {effectiveViewMode === 'table' ? (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected;
                    }}
                    onChange={(e) => onSelectAll(e.target.checked)}
                    className="rounded border-slate-600"
                  />
                </th>
                <th className="px-4 py-3 w-16"></th>
                <SortableHeader field="name">Name</SortableHeader>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                  Category
                </th>
                <SortableHeader field="price" className="text-right">
                  Price
                </SortableHeader>
                <SortableHeader field="quantity" className="text-right">
                  Stock
                </SortableHeader>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {products.map((product) => (
                <motion.tr
                  key={product.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={clsx(
                    'hover:bg-gray-50 transition-colors',
                    selectedIds.includes(product.id) && 'bg-orange-50'
                  )}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(product.id)}
                      onChange={(e) => onSelectOne(product.id, e.target.checked)}
                      className="rounded border-slate-600"
                    />
                  </td>
                  <td className="px-4 py-3">
                    {product.imageUrl ? (
                      <img
                        src={product.imageUrl}
                        alt={product.name}
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                        <Package size={16} className="text-slate-400" />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-slate-900 truncate max-w-xs">{product.name}</p>
                      {product.code && (
                        <p className="text-xs text-slate-600 font-mono">{product.code}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-600">
                      {product.category?.name || product.categoryName || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="font-semibold text-slate-900">
                      {product.currency === 'EUR' ? '€' : product.currency}{' '}
                      {product.price.toFixed(2)}
                    </span>
                    {product.compareAtPrice && (
                      <span className="text-xs text-slate-600 line-through ml-2">
                        {product.compareAtPrice.toFixed(2)}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span
                      className={clsx(
                        'font-medium',
                        product.quantity === 0
                          ? 'text-red-600'
                          : product.quantity <= product.lowStockThreshold
                          ? 'text-orange-600'
                          : 'text-slate-700'
                      )}
                    >
                      {product.quantity}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span
                        className={clsx(
                          'inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-xs font-medium',
                          product.isActive
                            ? 'bg-green-50 text-green-700 border border-green-200'
                            : 'bg-gray-100 text-slate-700 border border-gray-200'
                        )}
                      >
                        {product.isActive ? 'Active' : 'Inactive'}
                      </span>
                      {product.isFeatured && <Star size={16} className="text-yellow-600 fill-yellow-600" />}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      {/* Quick Print Button */}
                      {onPrintLabel && (
                        <button
                          onClick={() => onPrintLabel(product)}
                          className="p-1.5 rounded hover:bg-green-50 transition-colors group"
                          title="Print Label"
                        >
                          <Printer size={16} className="text-slate-400 group-hover:text-green-600" />
                        </button>
                      )}
                      
                      {/* More Actions Menu */}
                      <button
                        onClick={(e) => handleMenuButtonClick(product.id, e.currentTarget)}
                        className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                      >
                        <MoreHorizontal size={16} className="text-slate-600" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        // Grid View - optimized for mobile
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 lg:gap-4">
          {products.map((product) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={clsx(
                'bg-white border border-gray-200 rounded-xl p-3 lg:p-4 transition-all',
                selectedIds.includes(product.id) && 'ring-2 ring-orange-500'
              )}
            >
              {/* Header with checkbox and status */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(product.id)}
                    onChange={(e) => onSelectOne(product.id, e.target.checked)}
                    className="rounded border-gray-300 w-5 h-5"
                  />
                  {product.isFeatured && <Star size={18} className="text-yellow-600 fill-yellow-600" />}
                </div>
                <span
                  className={clsx(
                    'inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium',
                    product.isActive
                      ? 'bg-green-50 text-green-700 border border-green-200'
                      : 'bg-gray-100 text-slate-700 border border-gray-200'
                  )}
                >
                  {product.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              {/* Product image - tap to edit */}
              <div 
                className="cursor-pointer active:opacity-80"
                onClick={() => onEdit(product)}
              >
                {product.imageUrl ? (
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="w-full h-28 lg:h-32 rounded-lg object-cover mb-2"
                  />
                ) : (
                  <div className="w-full h-28 lg:h-32 rounded-lg bg-gray-100 flex items-center justify-center mb-2">
                    <Package size={32} className="text-slate-400" />
                  </div>
                )}

                <h3 className="font-semibold text-slate-900 mb-1 truncate text-base">{product.name}</h3>
                {(product.category?.name || product.categoryName) && (
                  <p className="text-xs text-slate-600 mb-1">{product.category?.name || product.categoryName}</p>
                )}
              </div>

              {/* Price and stock */}
              <div className="flex items-center justify-between mt-2 mb-3">
                <p className="text-lg lg:text-xl font-bold text-orange-600">
                  {product.currency === 'EUR' ? '€' : product.currency}
                  {product.price.toFixed(2)}
                </p>
                <p
                  className={clsx(
                    'text-sm font-medium',
                    product.quantity === 0
                      ? 'text-red-600'
                      : product.quantity <= product.lowStockThreshold
                      ? 'text-orange-600'
                      : 'text-slate-600'
                  )}
                >
                  Stock: {product.quantity}
                </p>
              </div>

              {/* Mobile action buttons */}
              <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                <button
                  onClick={() => onEdit(product)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-gray-50 hover:bg-gray-100 active:bg-gray-200 text-slate-700 text-sm font-medium transition-colors"
                >
                  <Edit size={16} />
                  <span>Edit</span>
                </button>
                {onPrintLabel && (
                  <button
                    onClick={() => onPrintLabel(product)}
                    className="flex items-center justify-center p-2 rounded-lg bg-green-50 hover:bg-green-100 active:bg-green-200 text-green-700 transition-colors"
                    title="Print Label"
                  >
                    <Printer size={18} />
                  </button>
                )}
                <button
                  onClick={() => onDuplicate(product.id)}
                  className="flex items-center justify-center p-2 rounded-lg bg-gray-50 hover:bg-gray-100 active:bg-gray-200 text-slate-600 transition-colors"
                  title="Duplicate"
                >
                  <Copy size={18} />
                </button>
                <button
                  onClick={() => {
                    if (confirm('Delete this product?')) {
                      onDelete(product.id);
                    }
                  }}
                  className="flex items-center justify-center p-2 rounded-lg bg-red-50 hover:bg-red-100 active:bg-red-200 text-red-600 transition-colors"
                  title="Delete"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Pagination - Mobile optimized */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-gray-200">
        {/* Page size selector - hidden on mobile */}
        <div className="hidden sm:flex items-center gap-2">
          <span className="text-sm text-slate-600">Rows per page:</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="bg-white border border-gray-300 rounded px-2 py-1 text-sm text-slate-900"
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        {/* Page navigation */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className="p-2 sm:p-1.5 rounded bg-white border border-gray-300 text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 active:bg-gray-200"
          >
            <ChevronLeft size={20} className="sm:w-4 sm:h-4" />
          </button>
          <span className="text-sm text-slate-600 min-w-[80px] text-center">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            className="p-2 sm:p-1.5 rounded bg-white border border-gray-300 text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 active:bg-gray-200"
          >
            <ChevronRight size={20} className="sm:w-4 sm:h-4" />
          </button>
        </div>
      </div>

      {/* Dropdown Menu Portal */}
      {openMenuId && menuPosition && createPortal(
        <>
          <div
            className="fixed inset-0 z-[9998]"
            onClick={() => setOpenMenuId(null)}
          />
          <div 
            className="fixed bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] py-1 min-w-[140px]"
            style={{ top: menuPosition.top, left: menuPosition.left }}
          >
            <button
              onClick={() => {
                const product = products.find(p => p.id === openMenuId);
                if (product) onEdit(product);
                setOpenMenuId(null);
              }}
              className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-gray-100 flex items-center gap-2"
            >
              <Edit size={16} />
              Edit
            </button>
            {onView && (
              <button
                onClick={() => {
                  const product = products.find(p => p.id === openMenuId);
                  if (product) onView(product);
                  setOpenMenuId(null);
                }}
                className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-gray-100 flex items-center gap-2"
              >
                <Eye size={16} />
                View
              </button>
            )}
            <button
              onClick={() => {
                onDuplicate(openMenuId);
                setOpenMenuId(null);
              }}
              className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-gray-100 flex items-center gap-2"
            >
              <Copy size={16} />
              Duplicate
            </button>
            <hr className="my-1 border-gray-200" />
            <button
              onClick={() => {
                if (confirm('Delete this product?')) {
                  onDelete(openMenuId);
                }
                setOpenMenuId(null);
              }}
              className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
            >
              <Trash2 size={16} />
              Delete
            </button>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
